/** The chat in numbers: the whole group's total counts up, then its busiest day and quietest week slide in. */

import { Fit, Scene, Zone, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import type { Chat } from "../../types";
import { count, shortDate } from "../shared";

const BLOBS: BlobSpec[] = [
  { colour: "surface-2", x: 980, y: 300, r: 220, seed: 27.4, wob: 0.2 },
  { colour: "yellow", x: 1000, y: 1820, r: 240, seed: 27.9, wob: 0.2 },
];

const TILE_TOPS = [760, 1130];

const dayOf = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
};

/** "Sat 14 Mar". */
const dayLabel = (iso: string) => `${dayOf(iso).toLocaleDateString("en-GB", { weekday: "short" })} ${shortDate(iso)}`;

/** A Monday-to-Sunday week: "20 – 26 Jul", or "27 Jul – 2 Aug" across a month end. */
function weekLabel(start: string) {
  const first = dayOf(start);
  const last = new Date(first.getFullYear(), first.getMonth(), first.getDate() + 6);
  const month = (d: Date) => d.toLocaleDateString("en-GB", { month: "short" });
  return month(first) === month(last)
    ? `${first.getDate()} – ${last.getDate()} ${month(last)}`
    : `${first.getDate()} ${month(first)} – ${last.getDate()} ${month(last)}`;
}

const messages = (n: number) => `${count(n)} ${n === 1 ? "message" : "messages"}`;

type Tile = { key: string; from: "left" | "right"; colour: string; title: string; when: string; line: string };

export function ChatNumbers({ numbers }: { numbers: Chat["numbers"] }) {
  const { busiest_day: busiest, quietest_week: quietest } = numbers;
  // Either tile can be missing; whichever is there takes the first slot.
  const tiles: Tile[] = [];
  if (busiest)
    tiles.push({
      key: "busiest",
      from: "right",
      colour: "var(--wr-fuchsia)",
      title: "BUSIEST DAY",
      when: dayLabel(busiest.date),
      line: `${messages(busiest.count)} in one day.`,
    });
  if (quietest)
    tiles.push({
      key: "quietest",
      from: "left",
      colour: "var(--wr-cyan)",
      title: "QUIETEST WEEK",
      when: weekLabel(quietest.start),
      line: quietest.count ? `Just ${messages(quietest.count)} all week.` : "Not a single message all week.",
    });

  const root = useTimeline((s) => {
    s.rise(s.q("[data-part=head]"), 0.35, { y: 30, stagger: 0.12 });
    s.rise(".total", 0.45, { y: 40, sound: false });
    s.count(s.q(".total")[0], numbers.messages, 0.45, 1.5, undefined, { ease: "power3.out" });
    s.rise("[data-part=people]", 1.0, { y: 24 });
    // Busiest day sweeps in from the right, quietest week from the left.
    s.q(".tile").forEach((tile, i) => {
      const at = 1.5 + i * 0.6;
      const x = tile.dataset.from === "right" ? 1100 : -1100;
      s.tl.fromTo(tile, { x, autoAlpha: 0 }, { x: 0, autoAlpha: 1, duration: 0.6, ease: "power4.out" }, at);
      s.sound("whoosh", at, 1, { pan: tile.dataset.from === "right" ? 0.7 : -0.7, length: 0.45 });
    });
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        <Zone at="top">
          <div data-part="head" className="kick" style={{ color: "var(--wr-yellow)" }}>
            The chat in numbers
          </div>
          <div data-part="head" className="lead">
            Now the whole group, together
          </div>
        </Zone>
        <Zone at="middle">
          <Fit
            size={270}
            min={150}
            className="abs disp total"
            style={{ left: 60, right: 60, top: 360, color: "var(--wr-yellow)", fontVariantNumeric: "tabular-nums" }}
          >
            {count(numbers.messages)}
          </Fit>
          <div data-part="people" className="line" style={{ top: 620 }}>
            messages from {count(numbers.people)} {numbers.people === 1 ? "person" : "people"}.
          </div>
        </Zone>
        <Zone at="bottom">
          {tiles.map((t, i) => (
            <div key={t.key} className="tile" data-from={t.from} style={{ top: TILE_TOPS[i], background: t.colour }}>
              <div className="mono">{t.title}</div>
              <Fit size={130} min={70} className="disp" style={{ marginTop: 16 }}>
                {t.when}
              </Fit>
              <p>{t.line}</p>
            </div>
          ))}
        </Zone>
      </Scene>
    </div>
  );
}
